import { useState } from 'react';
import { ApiError } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { useApiError } from '../lib/useApiError';
import { can, useSession } from '../lib/session';
import { useSaveBooking, useAssignees, type BookingInput } from '../lib/queries';
import type { Booking } from '../lib/types';
import { Alert, Button, Field, Select } from './ui';

/** `datetime-local` wants wall-clock text; the API wants an instant. */
export const toLocalInput = (date: Date): string => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

/** The parts of a booking the form edits; the calendar's copy carries exactly these. */
export type EditableBooking = Pick<Booking,
  'id' | 'name' | 'phone' | 'vehicleNote' | 'request' | 'expectedAt' | 'durationMinutes' | 'assignedAdminId'>;

const MINUTES_PER_DAY = 24 * 60;

/**
 * The lengths a slot is usually booked for; anything else is rare enough to round.
 * They run into days for the long jobs — a correction or a ceramic coat holds a car a while.
 */
const DURATIONS = [30, 45, 60, 90, 120, 180, 240, 300, 360, 480, 600, 720, 1440, 2880, 4320, 7200] as const;

const hoursText = (minutes: number): string => {
  const hours = minutes / 60;
  return Number.isInteger(hours) ? String(hours) : hours.toLocaleString('ru-RU', { maximumFractionDigits: 1 });
};

export function useDurationLabel() {
  const { t } = useI18n();
  return (minutes: number): string => {
    if (minutes < 60) return `${minutes} ${t('booking.minutesShort')}`;
    if (minutes >= MINUTES_PER_DAY) {
      const days = Math.floor(minutes / MINUTES_PER_DAY);
      const rest = minutes % MINUTES_PER_DAY;
      return rest >= 30
        ? `${days} ${t('booking.daysShort')} ${hoursText(rest)} ${t('booking.hoursShort')}`
        : `${days} ${t('booking.daysShort')}`;
    }
    return `${hoursText(minutes)} ${t('booking.hoursShort')}`;
  };
}

/**
 * Writing somebody down: who, when, for how long, and whose day it lands in.
 * Only who and when are required — a booking starts incomplete on purpose.
 */
export function BookingForm({ booking, startsAt, assignee, onDone }: {
  booking?: EditableBooking | undefined;
  /** For a slot tapped on the calendar. */
  startsAt?: Date | undefined;
  /** A new booking made in a technician's column lands in their day. */
  assignee?: string | null | undefined;
  onDone: () => void;
}) {
  const { t } = useI18n();
  const { data: session } = useSession();
  const mayAssign = can(session, 'orders.assign');
  const assignees = useAssignees(mayAssign);
  const save = useSaveBooking();
  const errorText = useApiError();
  const durationLabel = useDurationLabel();

  const [form, setForm] = useState({
    name: booking?.name ?? '',
    phone: booking?.phone ?? '',
    vehicleNote: booking?.vehicleNote ?? '',
    request: booking?.request ?? '',
    expectedAt: toLocalInput(booking ? new Date(booking.expectedAt) : startsAt ?? new Date(Date.now() + 2 * 3600_000)),
    durationMinutes: booking?.durationMinutes ?? 60,
    assignedAdminId: booking?.assignedAdminId ?? assignee ?? '',
  });

  const set = (patch: Partial<typeof form>) => setForm((prev) => ({ ...prev, ...patch }));
  // A column picked on the calendar may be someone this person cannot put on a job.
  const options = assignees(booking?.assignedAdminId);
  const assignedTo = options.length === 0 || options.some((p) => p.id === form.assignedAdminId) ? form.assignedAdminId : '';
  const ready = form.name.trim().length > 0 && form.expectedAt.length > 0;

  return (
    <div>
      {save.error instanceof ApiError && (
        <div className="mb-3"><Alert>{errorText(save.error) ?? save.error.message}</Alert></div>
      )}

      <div className="space-y-3">
        <Field
          label={t('booking.name')}
          value={form.name}
          autoFocus={!booking}
          onChange={(e) => set({ name: e.target.value })}
        />
        <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_7.5rem] sm:gap-2">
          <Field
            label={t('booking.when')}
            type="datetime-local"
            value={form.expectedAt}
            onChange={(e) => set({ expectedAt: e.target.value })}
          />
          <Select
            label={t('booking.duration')}
            value={String(form.durationMinutes)}
            onChange={(e) => set({ durationMinutes: Number(e.target.value) })}
          >
            {[...new Set([...DURATIONS, form.durationMinutes])].sort((a, b) => a - b).map((minutes) => (
              <option key={minutes} value={minutes}>{durationLabel(minutes)}</option>
            ))}
          </Select>
        </div>
        {mayAssign && (
          <Select
            label={t('booking.master')}
            value={assignedTo}
            onChange={(e) => set({ assignedAdminId: e.target.value })}
          >
            <option value="">{t('floor.unassigned')}</option>
            {options.map((person) => (
              <option key={person.id} value={person.id}>{person.firstName} {person.lastName}</option>
            ))}
          </Select>
        )}
        <Field
          label={t('booking.phone')}
          inputMode="tel"
          value={form.phone}
          onChange={(e) => set({ phone: e.target.value })}
        />
        <Field
          label={t('booking.vehicle')}
          value={form.vehicleNote}
          onChange={(e) => set({ vehicleNote: e.target.value })}
        />
        <Field
          label={t('booking.request')}
          value={form.request}
          onChange={(e) => set({ request: e.target.value })}
        />
      </div>

      <div className="mt-4 flex gap-2">
        <Button
          className="flex-1"
          disabled={!ready}
          loading={save.isPending}
          onClick={() => save.mutate({
            ...(booking ? { id: booking.id } : {}),
            name: form.name.trim(),
            phone: form.phone.trim(),
            vehicleNote: form.vehicleNote.trim(),
            request: form.request.trim(),
            expectedAt: new Date(form.expectedAt).toISOString(),
            durationMinutes: form.durationMinutes,
            ...(mayAssign ? { assignedAdminId: assignedTo || null } : {}),
          } satisfies BookingInput & { id?: string }, { onSuccess: onDone })}
        >
          {booking ? t('common.save') : t('booking.add')}
        </Button>
        <Button variant="ghost" onClick={onDone}>{t('common.cancel')}</Button>
      </div>
    </div>
  );
}
